import React from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';

export type SeoLandingContent = {
  path: string;
  title: string;
  description: string;
  keywords: string;
  h1: string;
  subhead: string;
  sections: Array<{ heading: string; body: string; bullets?: string[] }>;
  faq: Array<{ q: string; a: string }>;
  relatedLinks: Array<{ label: string; to: string }>;
};

type Props = {
  content: SeoLandingContent;
};

const SeoLandingPage: React.FC<Props> = ({ content }) => {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title={content.title}
        description={content.description}
        keywords={content.keywords}
        url={content.path}
        structuredData={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: content.h1,
            description: content.description,
            url: `https://www.illinoislawnhub.com${content.path}`,
            isPartOf: {
              '@type': 'WebSite',
              name: 'LawnHub',
              url: 'https://www.illinoislawnhub.com',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: content.faq.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.a,
              },
            })),
          },
        ]}
      />

      <Box sx={{ bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider', py: { xs: 5, md: 7 } }}>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 800, mb: 2, color: 'primary.main', fontSize: { xs: '1.75rem', md: '2.5rem' } }}
          >
            {content.h1}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3, fontWeight: 400, lineHeight: 1.5 }}>
            {content.subhead}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/get-started')}
              sx={{ bgcolor: '#ff6f00', fontWeight: 'bold', '&:hover': { bgcolor: '#ff8f00' } }}
            >
              Get Started Free
            </Button>
            <Button variant="outlined" size="large" component={RouterLink} to="/provider-benefits">
              See Provider Benefits
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {content.sections.map((section) => (
          <Box key={section.heading} sx={{ mb: 5 }}>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 1.5, fontSize: { xs: '1.35rem', md: '1.75rem' } }}>
              {section.heading}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
              {section.body}
            </Typography>
            {section.bullets && section.bullets.length > 0 && (
              <List dense>
                {section.bullets.map((b) => (
                  <ListItem key={b} sx={{ py: 0.25, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={b} />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        ))}

        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, mb: 5 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
            Frequently asked questions
          </Typography>
          {content.faq.map((item) => (
            <Box key={item.q} sx={{ mb: 2.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {item.q}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                {item.a}
              </Typography>
            </Box>
          ))}
        </Paper>

        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
          Related
        </Typography>
        <Grid container spacing={1} sx={{ mb: 4 }}>
          {content.relatedLinks.map((link) => (
            <Grid item xs={12} sm={6} key={link.to}>
              <Button component={RouterLink} to={link.to} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                {link.label}
              </Button>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Ready to run your lawn care business on LawnHub?
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/register', { state: { userType: 'provider' } })}
            sx={{ bgcolor: '#ff6f00', fontWeight: 'bold', '&:hover': { bgcolor: '#ff8f00' } }}
          >
            Create Free Provider Account
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default SeoLandingPage;
